import { useEffect, useState, type FormEvent } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  createMessageThread,
  listMessageThreads,
  listMessages,
  markThreadRead,
  postMessage,
} from "../lib/api-modules/messages.js";
import type { MembershipRole, MessageThread } from "../lib/api-modules/types.js";
import { ErrorMessage } from "../components/auth/error-message.js";

/**
 * /messages — Slack-style household chat list + per-thread chat view.
 *
 *   Left rail: list of threads, sorted newest-activity-first, with
 *     per-thread unread badges (zero-width when 0). Polls every 60s
 *     for fresh unread counts.
 *   Right pane: messages in the selected thread; mark-read fires on
 *     mount + on every poll cycle while the thread is open.
 *   "New thread" inline form on the left rail.
 *
 * Messages are append-only in v1 — no edit/delete affordances.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

const POLL_INTERVAL_MS = 60_000;

export function MessagesPage() {
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Messages</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Spousal async conversations, anchored to entities.
          </p>
        </div>
      </header>

      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <aside className="md:col-span-1">
          {canWrite && <CreateThreadForm onCreated={(t) => setSelectedId(t.thread_id)} />}
          <ThreadList
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </aside>
        <main className="md:col-span-2">
          {selectedId ? (
            <ThreadView
              threadId={selectedId}
              canWrite={canWrite}
            />
          ) : (
            <p className="rounded-md border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
              Select a thread to read messages.
            </p>
          )}
        </main>
      </div>
    </div>
  );
}

function ThreadList({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const query = useQuery({
    queryKey: ["message-threads"] as const,
    queryFn: listMessageThreads,
    refetchInterval: POLL_INTERVAL_MS,
  });

  if (query.isPending) {
    return <p className="text-sm text-zinc-500">Loading threads…</p>;
  }
  if (query.isError) {
    return (
      <ErrorMessage>
        Couldn't load threads:{" "}
        {query.error instanceof Error ? query.error.message : "unknown"}
      </ErrorMessage>
    );
  }
  if (!query.data || query.data.threads.length === 0) {
    return (
      <p className="rounded-md border border-zinc-200 p-3 text-sm text-zinc-500 dark:border-zinc-800">
        No threads yet.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {query.data.threads.map((t) => (
        <li key={t.thread_id}>
          <button
            type="button"
            onClick={() => onSelect(t.thread_id)}
            data-testid={`thread-${t.thread_id}`}
            aria-current={selectedId === t.thread_id}
            className={
              selectedId === t.thread_id
                ? "flex w-full items-baseline justify-between gap-2 rounded-md border border-brand-500 bg-brand-50 px-3 py-2 text-left text-sm dark:bg-brand-950/30"
                : "flex w-full items-baseline justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900"
            }
          >
            <span className="flex-1 truncate font-medium">{t.title}</span>
            {t.unread_count > 0 && (
              <span className="shrink-0 rounded-full bg-brand-500 px-2 py-0.5 text-xs font-medium text-white">
                {t.unread_count}
              </span>
            )}
          </button>
          {t.last_message_preview && (
            <p className="ml-3 mt-0.5 truncate text-xs text-zinc-500">
              {t.last_message_preview}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function CreateThreadForm({
  onCreated,
}: {
  onCreated: (thread: MessageThread) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => createMessageThread({ title: title.trim() }),
    onSuccess: async (thread) => {
      setTitle("");
      await queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      onCreated(thread);
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't create thread. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-3 flex flex-col gap-2 rounded-md border border-zinc-200 p-3 dark:border-zinc-800"
    >
      <label
        htmlFor="new-thread-title"
        className="text-xs font-medium text-zinc-600 dark:text-zinc-400"
      >
        New thread
      </label>
      <input
        id="new-thread-title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Thread title"
        maxLength={200}
        className="w-full rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <button
        type="submit"
        disabled={mutation.isPending || title.trim().length === 0}
        className="rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {mutation.isPending ? "Creating…" : "Start thread"}
      </button>
      {inlineError && <ErrorMessage>{inlineError}</ErrorMessage>}
    </form>
  );
}

function ThreadView({
  threadId,
  canWrite,
}: {
  threadId: string;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["messages", threadId] as const,
    queryFn: () => listMessages(threadId),
    refetchInterval: POLL_INTERVAL_MS,
  });

  // Mark-read on mount + every poll cycle while the thread is open.
  useEffect(() => {
    let cancelled = false;
    const fire = () => {
      if (cancelled) return;
      void markThreadRead(threadId).then(() =>
        queryClient.invalidateQueries({ queryKey: ["message-threads"] }),
      );
    };
    fire();
    const id = setInterval(fire, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [threadId, queryClient]);

  if (query.isPending) {
    return <p className="text-sm text-zinc-500">Loading messages…</p>;
  }
  if (query.isError) {
    return (
      <ErrorMessage>
        Couldn't load messages:{" "}
        {query.error instanceof Error ? query.error.message : "unknown"}
      </ErrorMessage>
    );
  }
  if (!query.data) return null;

  return (
    <section
      aria-label={query.data.thread.title}
      className="rounded-md border border-zinc-200 dark:border-zinc-800"
    >
      <header className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
        <h2 className="text-base font-medium">{query.data.thread.title}</h2>
        <p className="mt-1 text-xs text-zinc-500">
          {query.data.thread.participant_user_ids.length} participants
        </p>
      </header>
      <div
        className="space-y-3 px-4 py-4"
        data-testid="messages-list"
      >
        {query.data.messages.length === 0 ? (
          <p className="text-sm text-zinc-500">No messages yet.</p>
        ) : (
          query.data.messages.map((m) => (
            <div
              key={m.message_id}
              className="rounded-md bg-zinc-50 px-3 py-2 dark:bg-zinc-900"
            >
              <p className="whitespace-pre-wrap text-sm">{m.body}</p>
              <p className="mt-1 text-xs text-zinc-500">
                {new Date(m.timestamp).toLocaleString()}
              </p>
            </div>
          ))
        )}
      </div>
      {canWrite && <ComposeForm threadId={threadId} />}
    </section>
  );
}

function ComposeForm({ threadId }: { threadId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => postMessage(threadId, { body: body.trim() }),
    onSuccess: async () => {
      setBody("");
      await queryClient.invalidateQueries({
        queryKey: ["messages", threadId],
      });
      await queryClient.invalidateQueries({ queryKey: ["message-threads"] });
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't send. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-800"
    >
      <label htmlFor="compose-body" className="sr-only">
        New message
      </label>
      <textarea
        id="compose-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a message…"
        rows={2}
        maxLength={10_000}
        className="w-full resize-y rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
        disabled={mutation.isPending}
      />
      <div className="mt-2 flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending || body.trim().length === 0}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Sending…" : "Send"}
        </button>
      </div>
      {inlineError && (
        <div className="mt-2">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
    </form>
  );
}
